import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  useTheme,
  alpha,
  Chip,
  LinearProgress,
  Stack,
  Avatar,
  AvatarGroup,
  IconButton,
  Collapse,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import { MdChecklist, MdAccessTime, MdOutlineFolder } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { getSecureLocalItem } from "utils/secureSessionStorage";
import { selectCurrentUser } from "store/features/auth/authSelectors";

const priorityConfig = {
  urgent: {
    color: "#ef4444",
    bg: alpha("#ef4444", 0.12),
    label: "URGENT",
    chipBg: "#fee2e2",
    chipColor: "#ef4444",
  },
  high: {
    color: "#2563eb",
    bg: alpha("#2563eb", 0.12),
    label: "HIGH",
    chipBg: "#dbeafe",
    chipColor: "#2563eb",
  },
  medium: {
    color: "#4f46e5",
    bg: alpha("#4f46e5", 0.12),
    label: "MEDIUM",
    chipBg: "#e0e7ff",
    chipColor: "#4f46e5",
  },
  low: {
    color: "#64748b",
    bg: alpha("#64748b", 0.12),
    label: "LOW",
    chipBg: "#f1f5f9",
    chipColor: "#64748b",
  },
};

const statusConfig = {
  todo: { color: "#64748b", label: "To Do", bg: "#f1f5f9" },
  in_progress: { color: "#2563eb", label: "In Progress", bg: "#eff6ff" },
  review: { color: "#d97706", label: "In Review", bg: "#fffbeb" },
  done: { color: "#059669", label: "Done", bg: "#ecfdf5" },
  completed: { color: "#059669", label: "Done", bg: "#ecfdf5" },
};

// Helper to determine if a task is assigned to / created by current user
const isMyTask = (task, userId, employeeId, userName) => {
  if (!userId && !employeeId && !userName) return true;

  if (task.assigned_to !== undefined && task.assigned_to !== null) {
    if (Array.isArray(task.assigned_to)) {
      if (
        task.assigned_to.some(
          (id) =>
            String(id) === String(userId) || String(id) === String(employeeId),
        )
      ) {
        return true;
      }
    } else if (typeof task.assigned_to === "object") {
      if (
        String(task.assigned_to.id) === String(userId) ||
        String(task.assigned_to.id) === String(employeeId) ||
        (task.assigned_to.name &&
          userName &&
          task.assigned_to.name.toLowerCase() === userName.toLowerCase())
      ) {
        return true;
      }
    } else {
      const assignedStr = String(task.assigned_to);
      if (assignedStr.includes(",")) {
        const ids = assignedStr.split(",").map((s) => s.trim());
        if (
          ids.some(
            (id) =>
              id === String(userId) ||
              id === String(employeeId) ||
              (userName && id.toLowerCase() === userName.toLowerCase()),
          )
        ) {
          return true;
        }
      } else if (
        assignedStr === String(userId) ||
        assignedStr === String(employeeId) ||
        (userName && assignedStr.toLowerCase() === userName.toLowerCase())
      ) {
        return true;
      }
    }
  }

  if (Array.isArray(task.assignees) && task.assignees.length > 0) {
    if (
      task.assignees.some(
        (a) =>
          String(a.employee_id || a.user_id || a.id) === String(userId) ||
          String(a.employee_id || a.user_id || a.id) === String(employeeId),
      )
    ) {
      return true;
    }
  }

  if (
    task.created_by &&
    (String(task.created_by) === String(userId) ||
      String(task.created_by) === String(employeeId))
  ) {
    return true;
  }
  if (
    task.user_id &&
    (String(task.user_id) === String(userId) ||
      String(task.user_id) === String(employeeId))
  ) {
    return true;
  }

  return false;
};

const TopPriorityTasks = ({ tasks = [], userRole: propUserRole }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";

  const currentUser = useSelector(selectCurrentUser);
  const userRaw = getSecureLocalItem("user");
  const storedUser = userRaw
    ? typeof userRaw === "string"
      ? JSON.parse(userRaw)
      : userRaw
    : null;
  const user = storedUser || currentUser;

  const userId = user?.id || user?.user_id;
  const employeeId = user?.employee_id || user?.id;
  const userName =
    user?.name ||
    (user?.first_name
      ? `${user.first_name} ${user.last_name || ""}`.trim()
      : null);

  const activeRole = (
    propUserRole ||
    getSecureLocalItem("role") ||
    currentUser?.role ||
    currentUser?.role_name ||
    "Company"
  )
    .toString()
    .toLowerCase();

  const isCompanyRole =
    activeRole === "company" ||
    activeRole === "admin" ||
    activeRole === "super_admin" ||
    activeRole === "companyadmin";

  const [expandedTaskId, setExpandedTaskId] = useState(null);

  // Filter and sort top priority tasks (show top 5 in list)
  const priorityTasks = useMemo(() => {
    if (!tasks || !Array.isArray(tasks)) return [];

    const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };

    let list = tasks.filter(
      (task) =>
        task.is_parent_task === 0 ||
        task.is_parent_task === null ||
        task.is_parent_task === undefined,
    );

    if (!isCompanyRole) {
      list = list.filter((task) =>
        isMyTask(task, userId, employeeId, userName),
      );
    }

    list.sort((a, b) => {
      if (a.due_date && b.due_date) {
        const dateA = new Date(a.due_date).getTime();
        const dateB = new Date(b.due_date).getTime();
        if (dateA !== dateB) return dateA - dateB;
      } else if (a.due_date && !b.due_date) {
        return -1;
      } else if (!a.due_date && b.due_date) {
        return 1;
      }

      const pA = priorityOrder[a.priority?.toLowerCase()] ?? 99;
      const pB = priorityOrder[b.priority?.toLowerCase()] ?? 99;
      return pA - pB;
    });

    return list.slice(0, 6);
  }, [tasks, isCompanyRole, userId, employeeId, userName]);

  const formatDueDate = (dateString) => {
    if (!dateString) return "No due date";
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(dateString);
    target.setHours(0, 0, 0, 0);

    const diffDays = Math.ceil((target - today) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return `Overdue by ${Math.abs(diffDays)}d`;
    if (diffDays === 0) return "Due Today";
    if (diffDays === 1) return "Due Tomorrow";
    return `Due ${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  };

  const getTaskProgress = (task) => {
    if (task.progress_percentage !== undefined && task.progress_percentage !== null) {
      return Math.min(100, Math.max(0, Number(task.progress_percentage)));
    }
    const status = String(task.status || "").toLowerCase();
    if (status === "done" || status === "completed") return 100;
    if (status === "review") return 85;

    const est = parseFloat(task.estimated_hours || 0);
    const act = parseFloat(task.actual_hours || 0);
    if (est > 0 && act > 0) {
      return Math.min(100, Math.round((act / est) * 100));
    }
    if (status === "in_progress") return 50;
    return 15;
  };

  const toggleExpand = (e, taskId) => {
    e.stopPropagation();
    setExpandedTaskId((prev) => (prev === taskId ? null : taskId));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: "8px",
        bgcolor: isDark ? "#111827" : "#ffffff",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
        boxShadow: isDark
          ? "0 1px 3px rgba(0,0,0,0.4)"
          : "0 1px 3px rgba(15,23,42,0.03)",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Header Bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          pb: 1.5,
          borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9"}`,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "4px",
              bgcolor: isDark ? alpha("#4f46e5", 0.2) : "#eef2ff",
              color: "#4f46e5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MdChecklist size={18} />
          </Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              fontSize: "1rem",
              letterSpacing: "-0.01em",
            }}
          >
            Top Priority Tasks
          </Typography>
          <Chip
            label="Sorted by Urgency"
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "0.7rem",
              height: 22,
              borderRadius: "4px",
              bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9",
              color: "text.secondary",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
            }}
          />
        </Stack>

        <Typography
          variant="caption"
          onClick={() => navigate("/company/tasks")}
          sx={{
            fontWeight: 600,
            color: "#4f46e5",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          View Task Board &gt;
        </Typography>
      </Box>

      {/* Task Cards List */}
      {priorityTasks.length === 0 ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            {isCompanyRole
              ? "No priority tasks found across projects."
              : "No high-priority tasks currently assigned to you."}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {priorityTasks.map((task) => {
            const pKey = (task.priority || "medium").toLowerCase();
            const priority = priorityConfig[pKey] || priorityConfig.medium;
            const sKey = (task.status || "todo").toLowerCase();
            const status = statusConfig[sKey] || statusConfig.todo;
            const progress = getTaskProgress(task);
            const isExpanded = expandedTaskId === task.id;
            const dueDateText = formatDueDate(task.due_date);
            const isOverdue = dueDateText.startsWith("Overdue");

            const assignees = Array.isArray(task.assignees) && task.assignees.length > 0
              ? task.assignees
              : task.assigned_to_name
              ? [{ name: task.assigned_to_name, avatar: task.assigned_to_avatar }]
              : [];

            return (
              <Box
                key={task.id}
                sx={{
                  p: 1.75,
                  borderRadius: "6px",
                  bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#ffffff",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "#e5e7eb"}`,
                  transition: "all 0.15s ease",
                  "&:hover": {
                    borderColor: isDark ? "rgba(255,255,255,0.18)" : "#cbd5e1",
                    boxShadow: isDark
                      ? "0 2px 8px rgba(0,0,0,0.25)"
                      : "0 2px 6px rgba(15,23,42,0.04)",
                  },
                }}
              >
                {/* Row 1: Badges & Due Date & Status */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    {/* Priority Badge */}
                    <Box
                      component="span"
                      sx={{
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        px: 1,
                        py: 0.25,
                        borderRadius: "4px",
                        bgcolor: isDark ? alpha(priority.color, 0.2) : priority.chipBg,
                        color: priority.chipColor,
                        letterSpacing: "0.03em",
                      }}
                    >
                      {priority.label}
                    </Box>

                    {/* Project / Milestone Badge */}
                    {(task.project_name || task.milestone_name) && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          px: 1,
                          py: 0.25,
                          borderRadius: "4px",
                          bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9",
                          color: isDark ? "#94a3b8" : "#475569",
                          border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <MdOutlineFolder size={12} />
                        {task.project_name || task.milestone_name}
                      </Box>
                    )}

                    {/* Due Date */}
                    {task.due_date && (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <MdAccessTime
                          size={13}
                          color={isOverdue ? "#ef4444" : "#64748b"}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            color: isOverdue ? "#ef4444" : "text.secondary",
                          }}
                        >
                          {dueDateText}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>

                  {/* Status Pill on the far right */}
                  <Box
                    component="span"
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      px: 1.25,
                      py: 0.25,
                      borderRadius: "4px",
                      bgcolor: isDark ? alpha(status.color, 0.15) : status.bg,
                      color: status.color,
                      border: `1px solid ${isDark ? alpha(status.color, 0.3) : alpha(status.color, 0.2)}`,
                    }}
                  >
                    {status.label}
                  </Box>
                </Box>

                {/* Row 2: Task Title */}
                <Typography
                  variant="subtitle2"
                  onClick={() => navigate("/company/tasks")}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.92rem",
                    color: "text.primary",
                    lineHeight: 1.35,
                    cursor: "pointer",
                    mb: 1.5,
                    "&:hover": { color: "#4f46e5" },
                  }}
                >
                  {task.title}
                </Typography>

                {/* Row 3: Progress Bar & Assignee Avatars */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  {/* Progress section */}
                  <Box sx={{ flex: 1, maxWidth: 320 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          color: "text.secondary",
                        }}
                      >
                        Milestone Completion
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          color: "text.primary",
                        }}
                      >
                        {progress}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{
                        height: 5,
                        borderRadius: "2px",
                        bgcolor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: "2px",
                          background:
                            progress === 100
                              ? "#059669"
                              : "linear-gradient(90deg, #4f46e5 0%, #3b82f6 100%)",
                        },
                      }}
                    />
                  </Box>

                  {/* Right side: Assignee avatars & expand toggle */}
                  <Stack direction="row" spacing={1} alignItems="center">
                    {assignees.length > 0 && (
                      <AvatarGroup
                        max={3}
                        sx={{
                          "& .MuiAvatar-root": {
                            width: 24,
                            height: 24,
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            border: `1.5px solid ${isDark ? "#111827" : "#ffffff"}`,
                          },
                        }}
                      >
                        {assignees.map((a, aIdx) => (
                          <Avatar
                            key={a.id || aIdx}
                            src={a.avatar || a.profile_pic_url}
                            alt={a.name}
                          >
                            {(a.name || "U")[0]?.toUpperCase()}
                          </Avatar>
                        ))}
                      </AvatarGroup>
                    )}

                    {task.description && (
                      <IconButton
                        size="small"
                        onClick={(e) => toggleExpand(e, task.id)}
                        sx={{
                          p: 0.5,
                          borderRadius: "4px",
                          color: "text.secondary",
                        }}
                        title={isExpanded ? "Hide notes" : "View notes"}
                      >
                        {isExpanded ? (
                          <KeyboardArrowUp fontSize="small" />
                        ) : (
                          <KeyboardArrowDown fontSize="small" />
                        )}
                      </IconButton>
                    )}
                  </Stack>
                </Box>

                {/* Collapse Description */}
                {task.description && (
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box
                      sx={{
                        mt: 1.5,
                        pt: 1.25,
                        borderTop: `1px dashed ${isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb"}`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ lineHeight: 1.5, display: "block" }}
                      >
                        {task.description}
                      </Typography>
                    </Box>
                  </Collapse>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
};

export default TopPriorityTasks;